import { useEffect, useState } from 'react';
import { Plus, Copy, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function ShortUrlsSettingsPage() {
  const { hasPermission } = useAuth();
  const canManageSettings = hasPermission('all');
  const shortUrlBase = process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api/s/`
    : `${window.location.origin}/api/s/`;

  const [shortUrls, setShortUrls] = useState([]);
  const [shortUrlLoading, setShortUrlLoading] = useState(true);
  const [shortUrlSaving, setShortUrlSaving] = useState(false);
  const [showShortUrlModal, setShowShortUrlModal] = useState(false);
  const [creatingShortSamples, setCreatingShortSamples] = useState(false);
  const [shortUrlForm, setShortUrlForm] = useState({
    name: '',
    original_url: '',
    code: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    is_active: true
  });

  const resetShortUrlForm = () => {
    setShortUrlForm({
      name: '',
      original_url: '',
      code: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_content: '',
      utm_term: '',
      is_active: true
    });
  };

  const fetchShortUrls = async () => {
    setShortUrlLoading(true);
    try {
      const response = await adminApi.getShortUrls();
      setShortUrls(response.data?.items || []);
      if ((response.data?.items || []).length === 0 && !localStorage.getItem('short_url_samples_created')) {
        await createSampleShortUrls();
      }
    } catch (error) {
      toast.error('Failed to load short URLs');
    } finally {
      setShortUrlLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageSettings) return;
    fetchShortUrls();
  }, [canManageSettings]);

  const createSampleShortUrls = async () => {
    setCreatingShortSamples(true);
    try {
      await adminApi.createShortUrl({
        name: 'Festival Offer',
        original_url: 'https://agniyaspices.com/offers',
        utm_source: 'whatsapp',
        utm_medium: 'message',
        utm_campaign: 'festival_offer',
        is_active: true
      });
      await adminApi.createShortUrl({
        name: 'New Product Launch',
        original_url: 'https://agniyaspices.com/new',
        utm_source: 'instagram',
        utm_medium: 'social',
        utm_campaign: 'new_launch',
        is_active: true
      });
      localStorage.setItem('short_url_samples_created', 'true');
      const response = await adminApi.getShortUrls();
      setShortUrls(response.data?.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sample links');
    } finally {
      setCreatingShortSamples(false);
    }
  };

  const handleSaveShortUrl = async () => {
    if (!shortUrlForm.original_url.trim()) {
      toast.error('Original URL is required');
      return;
    }
    setShortUrlSaving(true);
    try {
      await adminApi.createShortUrl({
        name: shortUrlForm.name || undefined,
        original_url: shortUrlForm.original_url,
        code: shortUrlForm.code || undefined,
        utm_source: shortUrlForm.utm_source || undefined,
        utm_medium: shortUrlForm.utm_medium || undefined,
        utm_campaign: shortUrlForm.utm_campaign || undefined,
        utm_content: shortUrlForm.utm_content || undefined,
        utm_term: shortUrlForm.utm_term || undefined,
        is_active: shortUrlForm.is_active
      });
      toast.success('Short URL created');
      setShowShortUrlModal(false);
      resetShortUrlForm();
      fetchShortUrls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create short URL');
    } finally {
      setShortUrlSaving(false);
    }
  };

  const handleCopyShortUrl = async (code) => {
    try {
      await navigator.clipboard.writeText(`${shortUrlBase}${code}`);
      toast.success('Short URL copied');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  if (!canManageSettings) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Short URLs</h1>
        <p className="text-[#A8A8A8] mt-1">Create short links with tracking for analysis</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg text-[#2D2420]">Short URLs</CardTitle>
            <CardDescription>Create short links with tracking for analysis</CardDescription>
          </div>
          <Button
            onClick={() => { resetShortUrlForm(); setShowShortUrlModal(true); }}
            className="bg-[#C97A5E] hover:bg-[#B06850]"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Short URL
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {shortUrlLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : shortUrls.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-[#A8A8A8] gap-2">
              <span>No short URLs yet</span>
              <Button
                variant="outline"
                onClick={createSampleShortUrls}
                disabled={creatingShortSamples}
                className="border-[#E5E5E5]"
              >
                {creatingShortSamples ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Sample Links
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Original URL</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Short URL</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Clicks</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {shortUrls.map((item) => (
                    <tr key={item.id} className="hover:bg-[#FAFAF9]/50">
                      <td className="py-3 px-4 text-[#2D2420] font-medium">{item.name || '-'}</td>
                      <td className="py-3 px-4 text-[#2D2420] max-w-xs truncate" title={item.original_url}>
                        {item.original_url}
                      </td>
                      <td className="py-3 px-4 text-[#2D2420]">
                        <a
                          href={`${shortUrlBase}${item.code}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#C97A5E] hover:underline"
                        >
                          {shortUrlBase}{item.code}
                        </a>
                      </td>
                      <td className="py-3 px-4 text-right text-[#2D2420]">{item.total_clicks || 0}</td>
                      <td className="py-3 px-4">
                        <Badge className={item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCopyShortUrl(item.code)}
                            className="text-[#5C4033] hover:bg-[#FAFAF9]"
                            title="Copy"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showShortUrlModal} onOpenChange={setShowShortUrlModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Create Short URL</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Name</Label>
              <Input
                value={shortUrlForm.name}
                onChange={(e) => setShortUrlForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Optional label"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Original URL *</Label>
              <Input
                value={shortUrlForm.original_url}
                onChange={(e) => setShortUrlForm((prev) => ({ ...prev, original_url: e.target.value }))}
                placeholder="https://example.com"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Custom Code</Label>
              <Input
                value={shortUrlForm.code}
                onChange={(e) => setShortUrlForm((prev) => ({ ...prev, code: e.target.value }))}
                placeholder="Optional short code"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Source</Label>
                <Input
                  value={shortUrlForm.utm_source}
                  onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_source: e.target.value }))}
                  placeholder="utm_source"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Medium</Label>
                <Input
                  value={shortUrlForm.utm_medium}
                  onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_medium: e.target.value }))}
                  placeholder="utm_medium"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Campaign</Label>
                <Input
                  value={shortUrlForm.utm_campaign}
                  onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_campaign: e.target.value }))}
                  placeholder="utm_campaign"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Content</Label>
                <Input
                  value={shortUrlForm.utm_content}
                  onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_content: e.target.value }))}
                  placeholder="utm_content"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Term</Label>
                <Input
                  value={shortUrlForm.utm_term}
                  onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_term: e.target.value }))}
                  placeholder="utm_term"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-[#5C4033]">Active</Label>
                  <p className="text-sm text-[#A8A8A8]">Enable short URL</p>
                </div>
                <Switch
                  checked={shortUrlForm.is_active}
                  onCheckedChange={(checked) => setShortUrlForm((prev) => ({ ...prev, is_active: checked }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleSaveShortUrl}
              disabled={shortUrlSaving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {shortUrlSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
